'use client'

import React, { useState, useEffect } from 'react'
import { HeaderSection } from '@/components/shared/header-section'
import { PageContainer } from '@/components/shared/page-container'
import { useLazyQuery } from '@apollo/client'
import { DataTableManual } from '../../components/shared/data-table-manual/data-table-manual'
import { columns } from './components/columns'
import { DataTableToolbar } from '../../components/shared/data-table-manual/data-table-toolbar-manual'
import { Button } from '@/components/ui/button'
import {
  DELETE_MANY_ADDITIONAL_COMPONENT_GROUPS,
  GET_ADDITIONAL_COMPONENT_GROUPS,
} from './additionalComponentGroupQueries'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import { useDeleteConfirmation } from '@/hooks/useDeleteConfirmation'
import { PageContent } from '@/components/shared/page-content'
import { getOrderSortParams } from '@/utils/orderUtils'
import { useLanguage } from '@/context/LanguageContext'
import AdditionalComponentGroupForm from './AdditionalComponentGroupForm'

export default function AdditionalComponentGroups() {
  const { language } = useLanguage()
  const [searchTerm, setSearchTerm] = useState('')
  const [tableData, setTableData] = useState([])
  const [tableDataCount, setTableDataCount] = useState(0)
  const [order, setOrder] = useState([])
  const [filters, setFilters] = React.useState<Map<string, Set<string>>>(
    new Map()
  )

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  })

  const [loadAdditionalComponentGroups, { data, error, loading }] =
    useLazyQuery(GET_ADDITIONAL_COMPONENT_GROUPS, {
      variables: {},
      fetchPolicy: 'cache-and-network',
    })

  const { onDelete } = useDeleteConfirmation({
    mutation: DELETE_MANY_ADDITIONAL_COMPONENT_GROUPS,
    refetchQueries: ['GET_ADDITIONAL_COMPONENT_GROUPS'],
    messages: {
      success: 'Additional component groups deleted successfully',
      error: 'Failed to delete additional component groups',
    },
    itemName: 'additional component group',
  })

  // Prepare filters and graphQL params to query data
  useEffect(() => {
    const queryFilter = Array.from(filters.entries())
      .map(([key, value]) => {
        if (!value.size) return null
        return {
          [key]: {
            in: Array.from(value),
          },
        }
      })
      .filter(Boolean)

    loadAdditionalComponentGroups({
      variables: {
        searchTerm,
        language,
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
        filters: queryFilter,
        orderBy: getOrderSortParams(order),
      },
    })
  }, [searchTerm, pagination, order, filters, language])

  useEffect(() => {
    setPagination({ ...pagination, pageIndex: 0 })
  }, [searchTerm, pagination.pageSize, order])

  useEffect(() => {
    if (data) {
      const dataParsed = data.findManyAdditionalComponentGroup.map(
        (item: any) => {
          const profile = item.additional_component_group_profiles[0]
          return {
            ...item,
            name: profile?.name || item.code,
            code: item.code,
            description: profile?.description,
            componentCount: item._count.additional_components,
            use_fabric_prices_for_components:
              item.use_fabric_prices_for_components,
            enable_custom_dimensions_for_components:
              item.enable_custom_dimensions_for_components,
          }
        }
      )

      setTableData(dataParsed)
      setTableDataCount(data.findManyAdditionalComponentGroupCount ?? 0)
    }
  }, [data])

  const openAdditionalComponentGroupForm = useOpenDrawer(
    AdditionalComponentGroupForm
  )

  return (
    <PageContent>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <HeaderSection title="Additional Component Groups" />
        <Button onClick={openAdditionalComponentGroupForm}>Create New +</Button>
      </div>

      <DataTableManual
        data={tableData}
        columns={columns}
        rowCount={tableDataCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
        onSortingChange={setOrder}
        onColumnFiltersChange={setFilters}
        onRowClick={openAdditionalComponentGroupForm}
        isLoading={loading}
        toolbar={(table: any) => (
          <DataTableToolbar
            table={table}
            config={{
              onDelete: onDelete,
              search: {
                placeholder: 'Filter additional component groups...',
                accessor: 'name',
                onSearch: setSearchTerm,
                onFilter: setFilters,
              },
              columns: [],
            }}
          />
        )}
      />
    </PageContent>
  )
}
